import { closestCenter, DndContext, PointerSensor, useSensor, useSensors } from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { FC, useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'react-hot-toast'
import IconButton from '../buttons/IconButton'

import { MiscellaneousParameterList } from '@aero-platform/shared'
import {
  useCreateMiscParameterPreset,
  useCreateStarCCMParameterPreset,
  useGetMiscParameterPresets,
  useGetStarCCMParameterPresets,
  useUpdateMiscParameterPreset,
  useUpdateStarCCMParameterPreset
} from '../../api/runconfig-API'
import { ReactComponent as HamburgerIcon } from '../../assets/svg/hamburger.svg'
import { ReactComponent as InfoIcon } from '../../assets/svg/info.svg'
import { protoService } from '../../proto/ProtoService'
import { BasicNameValue, StarCCMConfig, StarCCMParameterList } from '../../types/types'
import { cloneMiscellaneousParameterList, cloneStarCCMParameterList } from '../../types/util'
import { starCCMMacroSteps } from '../../utils/constants'
import CustomStepDropDown from './CustomStepDropdown'
import ParameterPresetManagerModal, {
  PresetTypeMisc,
  PresetTypeRegular
} from './parameter/ParameterPresetManagerModal'

type StarCCMRunConfigFormProps = {
  selectedSteps: BasicNameValue[]
  selectedStarCCMPreset: StarCCMParameterList
  selectedMiscPreset: MiscellaneousParameterList
  raceTeamId: string
  customMacros: string[]
  readOnly: boolean
  restrictPresets?: boolean // If true, disables add/edit for parameter presets
  // If true, every default macro step can be toggled on/off. Otherwise only steps that were
  // already selected may be toggled, so a run can narrow but not extend its methodology's steps.
  allowAllDefaultSteps?: boolean
  onConfigChange: (config: StarCCMConfig) => void
}

const StarCCMRunConfigForm: FC<StarCCMRunConfigFormProps> = ({
  selectedSteps = [],
  selectedStarCCMPreset,
  selectedMiscPreset,
  raceTeamId,
  customMacros,
  readOnly,
  restrictPresets = false,
  allowAllDefaultSteps = false,
  onConfigChange
}) => {
  // Component-scoped mutable config that is kept in sync and propagated via onConfigChange
  const localConfigRef = useRef<StarCCMConfig>(new StarCCMConfig())

  // Fetches data and populates states from server
  const [, fetchStarccmPresets] = useGetStarCCMParameterPresets()
  const [, fetchMiscPresets] = useGetMiscParameterPresets()
  const [, createStarCCMPreset] = useCreateStarCCMParameterPreset()
  const [, createMiscPreset] = useCreateMiscParameterPreset()
  const [, updateStarCCMPreset] = useUpdateStarCCMParameterPreset()
  const [, updateMiscPreset] = useUpdateMiscParameterPreset()

  const [allStarCCMPresets, setAllStarCCMPresets] = useState<StarCCMParameterList[]>([])
  const [allMiscPresets, setAllMiscPresets] = useState<MiscellaneousParameterList[]>([])
  const [selectedStarCCMPresetLocal, setSelectedStarCCMPresetLocal] =
    useState<StarCCMParameterList>()
  const [selectedMiscPresetLocal, setSelectedMiscPresetLocal] =
    useState<MiscellaneousParameterList>()

  const [defaultMacroSteps, setDefaultMacroSteps] = useState<BasicNameValue[]>([])
  const [steps, setSteps] = useState<BasicNameValue[]>([])
  const [additionalSteps, setAdditionalSteps] = useState<BasicNameValue[]>([])

  // Compute additionalMacroSteps from customMacros prop AND any custom steps in selectedSteps
  const additionalMacroSteps = useMemo(() => {
    // All custom steps from props
    const macroSet = new Set(customMacros)
    // Add any custom steps from selectedSteps that are not in customMacros
    const allCustoms = [
      ...customMacros,
      ...selectedSteps
        .filter(
          step =>
            !starCCMMacroSteps.some(
              macro => macro.value === step.value && macro.name === step.name
            ) && !macroSet.has(step.value)
        )
        .map(step => step.value)
    ]
    // Remove duplicates
    const uniqueCustoms = Array.from(new Set(allCustoms))
    return uniqueCustoms.map(macro => new BasicNameValue({ name: macro, value: macro }))
  }, [customMacros, selectedSteps])

  // Macro steps (unchanged)
  useEffect(() => {
    // Set all default macro steps at once (atomic)
    setDefaultMacroSteps(
      starCCMMacroSteps.map(step => new BasicNameValue({ name: step.name, value: step.value }))
    )
  }, [])

  // Initialize steps and additionalSteps only after both selectedSteps and defaultMacroSteps are ready
  useEffect(() => {
    if (selectedSteps && defaultMacroSteps.length > 0) {
      // Set steps from selectedSteps
      setSteps(selectedSteps)
      // Set additionalSteps as custom steps (not in default)
      const genAddSteps = selectedSteps.filter(
        step =>
          !defaultMacroSteps.some(
            defaultStep => defaultStep.name === step.name && defaultStep.value === step.value
          )
      )
      setAdditionalSteps(genAddSteps)
    } else {
      setSteps([])
      setAdditionalSteps([])
    }
  }, [selectedSteps, defaultMacroSteps])

  useEffect(() => {
    const localConfig = localConfigRef.current
    if (steps) {
      localConfig.macroSteps = steps.map(
        step => new BasicNameValue({ name: step.name, value: step.value })
      )
      localConfig.customSteps = steps
        .filter(
          step => !defaultMacroSteps.some(ds => ds.name === step.name && ds.value === step.value)
        )
        .map(step => step.value)
      localConfig.stepOrder = steps.map(step => step.value)
    } else {
      localConfig.macroSteps = []
      localConfig.customSteps = []
      localConfig.stepOrder = []
    }
    onConfigChange && onConfigChange(localConfig)
  }, [steps])

  useEffect(() => {
    const localConfig = localConfigRef.current
    if (selectedStarCCMPresetLocal) {
      localConfig.starccmParameters = cloneStarCCMParameterList(selectedStarCCMPresetLocal)
    }
    onConfigChange && onConfigChange(localConfig)
  }, [selectedStarCCMPresetLocal])

  useEffect(() => {
    const localConfig = localConfigRef.current
    if (selectedMiscPresetLocal) {
      localConfig.miscellaneousParameters = cloneMiscellaneousParameterList(selectedMiscPresetLocal)
    }
    onConfigChange && onConfigChange(localConfig)
  }, [selectedMiscPresetLocal])

  // Fetch StarCCM presets
  useEffect(() => {
    const fetchStarccmPresetsList = async () => {
      const starccmPresets: StarCCMParameterList[] = []
      try {
        const starccmPresetRes = await fetchStarccmPresets()
        if (
          starccmPresetRes.data.protoBytesList &&
          starccmPresetRes.data.protoBytesList.length > 0
        ) {
          starccmPresetRes.data.protoBytesList.forEach(item => {
            const preset = protoService.deSerialize(
              item,
              'starccmparameterlist'
            ) as StarCCMParameterList
            if (preset) {
              starccmPresets.push(preset)
            }
          })
        }
      } catch {}
      setAllStarCCMPresets(prev => [...prev, ...starccmPresets])
    }
    fetchStarccmPresetsList()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Fetch Miscellaneous presets
  useEffect(() => {
    const fetchMiscPresetsList = async () => {
      let miscPresets: MiscellaneousParameterList[] = []
      try {
        const miscPresetRes = await fetchMiscPresets()
        if (
          miscPresetRes.data.protoBytesList &&
          Array.isArray(miscPresetRes.data.protoBytesList) &&
          miscPresetRes.data.protoBytesList.length > 0
        ) {
          miscPresetRes.data.protoBytesList.forEach(item => {
            const preset = protoService.deSerialize(
              item,
              'miscellaneousparameterlist'
            ) as MiscellaneousParameterList
            if (preset) {
              miscPresets.push(preset)
            }
          })
        }
      } catch {}
      setAllMiscPresets(prev => [...prev, ...miscPresets])
    }
    fetchMiscPresetsList()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    // Always derive additionalSteps from selectedMacroSteps and defaultMacroSteps
    if (selectedSteps?.length > 0 && defaultMacroSteps.length > 0) {
      const genAddSteps = selectedSteps.filter(
        step =>
          !defaultMacroSteps.some(
            defaultStep => defaultStep.name === step.name && defaultStep.value === step.value
          )
      )
      setAdditionalSteps(genAddSteps)
      setSteps(selectedSteps)
    } else {
      setSteps([])
    }
    // Only run when selectedSteps changes, and when defaultMacroSteps is populated
  }, [selectedSteps, defaultMacroSteps.length])

  useEffect(() => {
    if (defaultMacroSteps.length > 0) {
      setAdditionalSteps(
        steps.filter(
          step =>
            !defaultMacroSteps.some(
              defaultStep => defaultStep.name === step.name && defaultStep.value === step.value
            )
        )
      )
    }
  }, [steps, defaultMacroSteps.length])

  // Set selected Star Preset
  useEffect(() => {
    if (selectedStarCCMPreset) {
      setSelectedStarCCMPresetLocal(selectedStarCCMPreset)
    }
  }, [selectedStarCCMPreset])

  // Set selected Misc Preset
  useEffect(() => {
    if (selectedMiscPreset) {
      setSelectedMiscPresetLocal(selectedMiscPreset)
    }
  }, [selectedMiscPreset])

  // Sortable item for macro steps
  const SortableMacroStep = ({ id, step, index, isDraggable }) => {
    const sortable = useSortable({ id })
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = sortable
    // Prevent overflow to the right by clamping translateX to 0 or less
    let clampedTransform = transform
    if (transform && transform.x > 0) {
      clampedTransform = { ...transform, x: 0 }
    } else if (transform && transform.x < 0) {
      clampedTransform = { ...transform, x: 0 }
    }
    return (
      <div
        ref={setNodeRef}
        className={`
                    flex items-center min-h-[32px] mb-2 px-4 py-2 rounded-lg
                    font-semibold flex-1
                    ${
                      isDragging
                        ? 'bg-[#e0e7ef] shadow-lg'
                        : isDraggable
                        ? 'bg-gm-aqua-light text-white cursor-grab'
                        : 'bg-gm-blue text-white cursor-not-allowed select-none'
                    }
                    overflow-hidden
                `}
        style={{
          transition,
          transform: CSS.Transform.toString(clampedTransform)
        }}
        {...(isDraggable ? attributes : {})}
        {...(isDraggable ? listeners : {})}>
        <div className="flex font-semibold flex-1 gap-x-2 items-center">
          {isDraggable && (
            <span className="flex items-center justify-center">
              <HamburgerIcon className="w-2 h-2 flex-1 fill-white" />
            </span>
          )}
          <span className="flex flex-1 self-center">{step.name}</span>
        </div>
      </div>
    )
  }

  const pointerSensor = useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  const sensors = useSensors(pointerSensor)

  // Helper to get unique sortable IDs
  const getSortableId = (step: BasicNameValue, idx: number) =>
    step.value !== '' && `${step.value}-${idx}`

  const saveSelectedStarCCMPresetLocal = async (
    preset: StarCCMParameterList
  ): Promise<StarCCMParameterList | undefined> => {
    try {
      preset.isPreset = true
      preset.raceteamId = raceTeamId
      // Serialize the preset to protoBytes
      let newPreset
      if (preset._id) {
        newPreset = await updateStarCCMPreset({
          data: protoService.encodePayload(preset, 'starccmparameterlist')
        })
        console.log('Successfully updated StarCCM preset:', newPreset)
      } else {
        newPreset = await createStarCCMPreset({
          data: protoService.encodePayload(preset, 'starccmparameterlist')
        })
        console.log('Successfully created StarCCM preset:', newPreset)
      }
      toast.success(`Successfully saved StarCCM preset: ${preset.name}`)
      const result = newPreset.data.protoBytesList[0]
        ? (protoService.deSerialize(
            newPreset.data.protoBytesList[0],
            'starccmparameterlist'
          ) as StarCCMParameterList)
        : undefined
      if (!result) {
        throw new Error('Failed to save StarCCM preset')
      }
      return result
    } catch (error) {
      toast.error(`Error saving StarCCM preset: ${preset.name}`)
      console.error('Error creating StarCCM preset:', error)
    }
  }

  const saveSelectedMiscPresetLocal = async (preset: MiscellaneousParameterList) => {
    // setSelectedLocalMiscPreset(preset);
    try {
      preset.isPreset = true
      preset.raceteamId = raceTeamId
      let newPreset
      if (preset._id) {
        newPreset = await updateMiscPreset({
          data: protoService.encodePayload(preset, 'miscellaneousparameterlist')
        })
        toast.success(`Successfully saved misc preset: ${preset.name}`)
      } else {
        newPreset = await createMiscPreset({
          data: protoService.encodePayload(preset, 'miscellaneousparameterlist')
        })
        toast.success(`Successfully saved misc preset: ${preset.name}`)
      }
      const result = newPreset.data.protoBytesList[0]
        ? (protoService.deSerialize(
            newPreset.data.protoBytesList[0],
            'miscellaneousparameterlist'
          ) as MiscellaneousParameterList)
        : undefined
      if (!result) {
        throw new Error('Failed to save misc preset')
      }
      return result
    } catch (error) {
      console.error('Error saving miscellaneous preset:', error)
      toast.error(`Error saving misc preset: ${preset.name}`)
    }
  }

  return (
    <div className="w-full mr-2 flex flex-col items-stretch min-h-full max-h-full">
      <div className="flex flex-row gap-4 w-full my-4">
        {/* Section 1 */}
        <div className="space-y-4 w-3/4">
          <div id="macro-steps">
            <label htmlFor="macro-steps" className="block text-base">
              <span>StarCCM Macro Steps Selection</span>
            </label>
            {/* Checkbox with selected */}
            <div className="grid grid-cols-4 gap-x-8 gap-y-4 items-end py-2">
              {defaultMacroSteps.map((step, index) => {
                const canToggle =
                  allowAllDefaultSteps ||
                  selectedSteps.some(s => s.name === step.name && s.value === step.value)
                const isChecked = steps.some(s => s.name === step.name && s.value === step.value)
                return (
                  <div
                    key={index}
                    className={`flex items-center ${
                      canToggle ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'
                    }`}>
                    <input
                      type="checkbox"
                      id={`macro-step-${index}`}
                      disabled={readOnly || !canToggle}
                      checked={isChecked}
                      onChange={e => {
                        if (!canToggle) return // Should never happen, but extra guard
                        if (e.target.checked) {
                          setSteps(prev => {
                            if (!prev) return [step]
                            // Separate default and custom steps
                            const prevDefaultSteps = prev.filter(s =>
                              defaultMacroSteps.some(ds => ds.value === s.value)
                            )
                            // If step already present, do nothing
                            if (prevDefaultSteps.some(s => s.value === step.value)) return prev
                            // Find the index in defaultMacroSteps for the new step
                            const insertIdx = defaultMacroSteps.findIndex(
                              ds => ds.value === step.value
                            )
                            // Find where to insert in prev to preserve overall order
                            let newSteps = [...prev]
                            // Find the index in prev where the next default step (after insertIdx) is located
                            let nextDefaultStepIdx = -1
                            for (let i = 0; i < newSteps.length; i++) {
                              const s = newSteps[i]
                              const idxInDefault = defaultMacroSteps.findIndex(
                                ds => ds.value === s.value
                              )
                              if (idxInDefault > insertIdx) {
                                nextDefaultStepIdx = i
                                break
                              }
                            }
                            if (nextDefaultStepIdx === -1) {
                              // Insert at the end (before custom steps if any)
                              newSteps.push(step)
                            } else {
                              // Insert at the found index, shifting custom steps if needed
                              newSteps.splice(nextDefaultStepIdx, 0, step)
                            }
                            return newSteps
                          })
                        } else {
                          setSteps(prev =>
                            prev.filter(s => s.name !== step.name && s.value !== step.value)
                          )
                        }
                      }}
                      className="mr-2 h-4 w-4 text-gm-blue border-gray-300 rounded-full accent-gm-blue"
                    />
                    <label htmlFor={`macro-step-${index}`} className="flex items-center h-full">
                      <span className={steps.includes(step) ? 'font-bold' : 'font-normal'}>
                        {step.name}
                      </span>
                    </label>
                  </div>
                )
              })}
            </div>
          </div>
          <div id="custom-macro-steps">
            <label htmlFor="custom-macro-steps" className="block text-base py-2">
              <span>AeroCCM Custom Step (.java file)</span>
            </label>
            <div className="w-[100%]">
              {/* Show plus sign for adding custom Java steps only if not readOnly and not restrictPresets */}
              {additionalSteps.length === 0 ? (
                <CustomStepDropDown
                  step={new BasicNameValue({ name: '', value: '' })}
                  index={0}
                  additionalMacroSteps={additionalMacroSteps}
                  setSteps={setSteps}
                  readOnly={readOnly}
                  displayAdd={!readOnly && !restrictPresets}
                />
              ) : (
                additionalSteps.map((step, index) => (
                  <CustomStepDropDown
                    key={`selected-${index}`}
                    step={step}
                    index={index}
                    additionalMacroSteps={additionalMacroSteps}
                    setSteps={setSteps}
                    readOnly={readOnly}
                    displayAdd={
                      index === additionalSteps.length - 1 && !readOnly && !restrictPresets
                    }
                  />
                ))
              )}
            </div>
          </div>
          <div id="parameters-presets">
            <label htmlFor="parameters-presets" className="block text-base py-2">
              <span>StarCCM Parameters Presets</span>
            </label>
            {/* Disable adding new StarCCM parameter presets */}
            <ParameterPresetManagerModal
              title="StarCCM Parameters Presets"
              presetType={PresetTypeRegular}
              currentPreset={selectedStarCCMPresetLocal}
              presets={allStarCCMPresets}
              readOnly={restrictPresets ? true : false}
              allowLocalParameterValueEdit={!readOnly}
              saveSelectedPreset={saveSelectedStarCCMPresetLocal}
              updateCurrentSelectedPreset={preset =>
                setSelectedStarCCMPresetLocal(preset as StarCCMParameterList)
              }
            />
          </div>
          <div id="misc-parameters-presets">
            <label htmlFor="parameters-presets" className="block text-base py-2">
              <span>Misc Parameters Presets</span>
            </label>
            <ParameterPresetManagerModal
              title="Miscellaneous Parameters Presets"
              presetType={PresetTypeMisc}
              currentPreset={selectedMiscPresetLocal}
              presets={allMiscPresets}
              readOnly={restrictPresets ? true : false}
              allowLocalParameterValueEdit={!readOnly}
              saveSelectedPreset={saveSelectedMiscPresetLocal}
              updateCurrentSelectedPreset={preset =>
                setSelectedMiscPresetLocal(preset as MiscellaneousParameterList)
              }
            />
          </div>
        </div>
        {/* Section 2 */}
        <div className="w-1/4 mt-6">
          {/* Drag and Drop based on selected steps */}
          <div className="text-base flex items-center">
            <div>Selected Steps</div>
            <IconButton
              title="The steps you have selected for this run. Drag to reorder."
              additionalClassName=" inline"
              severity="help"
              clear
              icon={
                <InfoIcon className="w-6 h-6 dark:fill-white fill-gm-black inline cursor-pointer" />
              }
            />
          </div>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={event => {
              const { active, over } = event
              if (active && over && active.id !== over.id) {
                // Extract index from unique id
                const getIndex = id => parseInt(id.split('-').pop(), 10)
                const oldIndex = getIndex(active.id)
                const newIndex = getIndex(over.id)
                setSteps(prev => arrayMove(prev, oldIndex, newIndex))
              }
            }}>
            <SortableContext
              items={steps.filter(step => step.value !== '').map(getSortableId)}
              strategy={verticalListSortingStrategy}>
              <div className="flex flex-col gap-2 dark:bg-neutral-900 rounded-lg p-4 max-h-[400px] overflow-y-auto">
                {steps.filter(step => step.value !== '').length === 0 ? (
                  <div className="text-neutral-70 text-center py-8">
                    No steps selected. <br />
                    Use the checkboxes to add steps.
                  </div>
                ) : (
                  steps
                    .map((step, idx) => ({ step, idx }))
                    .filter(({ step }) => step.value !== '')
                    .map(({ step, idx }) => (
                      <SortableMacroStep
                        key={getSortableId(step, idx)}
                        id={getSortableId(step, idx)}
                        step={step}
                        index={idx}
                        isDraggable={
                          !readOnly &&
                          !defaultMacroSteps.find(defaultStep => defaultStep.value === step.value)
                        }
                      />
                    ))
                )}
              </div>
            </SortableContext>
          </DndContext>
        </div>
      </div>
    </div>
  )
}

export default StarCCMRunConfigForm
